import React, { Suspense } from "react";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";

import "us.style/antd.less";
import "us.style/index.scss";
import { Loader } from "us.common/components/Loader";

import App from "App";
import * as serviceWorker from "serviceWorker";
import store from "store";
import * as Azure from "us.helper/azure";
import {
  AuthenticationResult,
  EventMessage,
  EventType,
  PublicClientApplication,
} from "@azure/msal-browser";
import { setupTitle } from "us.helper";
import { MsalProvider } from "@azure/msal-react";
import "us.helper/utility/dayjsConfig";

// Set module identifier
window.APP_MODULE = "economy";

// Initialize MSAL
export const msalInstance = new PublicClientApplication(Azure.msalConfig);

// Handle initial account selection
const accounts = msalInstance.getAllAccounts();
if (!msalInstance.getActiveAccount() && accounts.length > 0) {
  msalInstance.setActiveAccount(accounts[0]);
}

// Sync account state across tabs
msalInstance.enableAccountStorageEvents();

// MSAL Event Callbacks
msalInstance.addEventCallback((event: EventMessage) => {
  if (
    [EventType.LOGIN_SUCCESS, EventType.SSO_SILENT_SUCCESS].includes(
      event.eventType
    ) &&
    event.payload
  ) {
    const account = (event.payload as AuthenticationResult).account;
    msalInstance.setActiveAccount(account);
  } else if (event.eventType === EventType.LOGOUT_SUCCESS) {
    msalInstance.setActiveAccount(null);
    window.location.reload();
  } else if (event.eventType === EventType.ACCOUNT_ADDED && event.payload) {
    const account = (event.payload as AuthenticationResult).account;
    msalInstance.setActiveAccount(account);
  } else if (event.eventType === EventType.ACCOUNT_REMOVED) {
    msalInstance.setActiveAccount(null);
    window.location.reload();
  }
});

// Setup dynamic title
setupTitle();

// Mount React App
const app = (
  <Provider store={store}>
    <MsalProvider instance={msalInstance}>
      <Suspense fallback={<Loader />}>
        <App msalInstance={msalInstance} />
      </Suspense>
    </MsalProvider>
  </Provider>
);

const container = document.getElementById("root");
if (container) {
  const root = createRoot(container);
  root.render(app);
}

// Service Worker (register or unregister based on your needs)
serviceWorker.register();
